import {useConfig} from '@aboutyou/config';
import {range} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {SliderControlProps} from '../../molecules/HorizontalSlider/types';

const MAX_VISIBLE_DOTS = 6;

export const SliderPaginationDots: React.FC<
  Pick<
    SliderControlProps,
    | 'currentSlide'
    | 'maxPages'
    | 'scrollToSlide'
    | 'autoplayDuration'
    | 'enlargeActiveBullet'
    | 'dotsColor'
    | 'isAnimationStopped'
    | 'isAnimationPaused'
  > & {
    className?: string;
    onClickDot?: VoidFunction;
  }
> = ({
  className,
  currentSlide,
  maxPages = MAX_VISIBLE_DOTS,
  scrollToSlide,
  autoplayDuration,
  enlargeActiveBullet,
  dotsColor = {
    active: COLORS['Fill.Black'],
    inactive: COLORS['Fill.Grey.Inactive'],
    progress: COLORS['Fill.Black'],
  },
  isAnimationStopped,
  isAnimationPaused,
  onClickDot,
}) => {
  const {start, end} = calculateVisibleRange(currentSlide, maxPages);
  const {device} = useConfig();
  const hasLeftOverflow = start > 0;
  const hasRightOverflow = end < maxPages;

  const handleOnClick =
    ({index, isActive}: {isActive: boolean; index: number}) =>
    () => {
      onClickDot?.();

      if (!isActive) {
        scrollToSlide(index);
      }
    };

  return (
    <Wrapper className={className} data-is-desktop={device === 'desktop'}>
      {hasLeftOverflow && (
        <>
          <DotWrapper>
            <Dot data-size="xs" dotsColor={dotsColor} />
          </DotWrapper>
          <DotWrapper>
            <Dot data-size="s" dotsColor={dotsColor} />
          </DotWrapper>
        </>
      )}
      {range(start, end).map(index => {
        const isActive = index === currentSlide;

        return (
          <DotWrapper key={index}>
            <Dot
              dotsColor={dotsColor}
              data-active={isActive}
              data-is-animation-stopped={isAnimationStopped}
              data-is-animation-paused={isAnimationPaused}
              data-enlarge-active={enlargeActiveBullet}
              data-show-progress={autoplayDuration && isActive}
              $autoplayDuration={autoplayDuration}
              onClick={handleOnClick({isActive, index})}
            />
          </DotWrapper>
        );
      })}
      {hasRightOverflow && (
        <>
          <DotWrapper>
            <Dot data-size="s" dotsColor={dotsColor} />
          </DotWrapper>
          <DotWrapper>
            <Dot data-size="xs" dotsColor={dotsColor} />
          </DotWrapper>
        </>
      )}
    </Wrapper>
  );
};

const calculateVisibleRange = (currentSlide: number, maxPages: number) => {
  let start = Math.max(0, currentSlide - Math.floor(MAX_VISIBLE_DOTS / 2));
  let end = start + MAX_VISIBLE_DOTS;

  if (end > maxPages) {
    end = maxPages;
    start = Math.max(0, end - MAX_VISIBLE_DOTS);
  }

  return {start, end};
};

const Wrapper = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-top: 16px;
  padding-bottom: 2px;
`;

const DotWrapper = styled.li`
  position: relative;
  list-style: none;
`;

const Dot = styled.div<
  {
    $autoplayDuration?: number;
  } & Pick<SliderControlProps, 'dotsColor'>
>`
  position: relative;
  list-style: none;
  width: 6px;
  height: 6px;
  background-color: ${props => props.dotsColor?.inactive ?? ''};
  border-radius: 100%;

  cursor: pointer;
  will-change: transform, opacity;
  overflow: hidden;

  // eslint-disable-next-line local-rules/css-layout-animation
  transition: width 0.3s cubic-bezier(0.65, 0, 0.35, 1),
    opacity 0.2s cubic-bezier(0.65, 0, 0.35, 1),
    border-radius 0.3s cubic-bezier(0.65, 0, 0.35, 1);

  &[data-active='true'] {
    border-radius: 4px;
    width: 16px;
    background-color: ${props => props.dotsColor?.active ?? ''};

    &[data-enlarge-active='true'] {
      width: 32px;
    }

    &[data-show-progress='true'] {
      background-color: ${props => props.dotsColor?.inactive ?? ''};

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 16px;
        height: 6px;
        background-color: ${props => props.dotsColor?.progress ?? ''};
        border-radius: 4px;
        z-index: 10;
        transform: translateX(-100%);
        animation: progress ${props => `${props.$autoplayDuration}s`} linear
          forwards;
      }

      &[data-enlarge-active='true']::after {
        width: 32px;
      }

      &[data-is-animation-paused='true']::after {
        animation-play-state: paused;
      }

      @keyframes progress {
        0% {
          transform: translateX(-100%);
          opacity: 0.2;
        }
        60% {
          opacity: 1;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
    }

    &[data-is-animation-stopped='true']::after {
      animation: none;
      transform: translateX(0%);
    }
  }

  &[data-size='s'] {
    width: 4px;
    height: 4px;
  }

  &[data-size='xs'] {
    width: 2px;
    height: 2px;
  }
`;
